import styled from "styled-components";
import { Box } from "rebass/styled-components";
import { colors } from "../../../theme/colors";
import { Button } from "../Button";

interface ISmartBannerContainerProps {
  $platform: "ios" | "android";
  $position: "top" | "bottom";
}

export const SmartBannerContainer = styled(Box)<ISmartBannerContainerProps>`
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: none;
  background: ${colors.ned};
  display: block;
  font-family: -apple-system, BlinkMacKitWeb, "Segoe UI", "Roboto",
    "Helvetica Neue", Helvetica, Arial, sans-serif;
  height: 80px;
  left: 0;
  line-height: ${(props) => (props.$platform === "android" ? "82px" : "80px")};
  overflow: hidden;
  width: 100%;
  z-index: 9998;

  ${(props) =>
    props.$position === "top" &&
    `
    position: absolute;
    top: 0;
  `}

  ${(props) =>
    props.$position === "bottom" &&
    `
    position: fixed;
    bottom: 0;
  `}

  ${(props) =>
    props.$platform === "ios" &&
    `
    box-shadow: 0 1px 1px ${colors.blackAlpha20};
  `}

  ${(props) =>
    props.$platform === "android" &&
    `
    background: ${colors.ned};
    box-shadow: 0 1px 1px ${colors.blackAlpha20};
  `}
`;

export const SmartBannerInnerContainer = styled(Box)`
  -ms-flex-align: center;
  -webkit-align-items: center;
  -webkit-box-align: center;
  align-items: center;
  display: -ms-flexbox;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  height: 100%;
  margin: 0 auto;
  padding: 0 5px;
`;

interface ISmartBannerCloseProps {
  $platform: "ios" | "android";
}

export const SmartBannerClose = styled.button<ISmartBannerCloseProps>`
  -webkit-font-smoothing: ${(props) =>
    props.$platform === "ios" ? "none" : "subpixel-antialiased"};
  background-color: transparent;
  border: 0;
  border-radius: 14px;
  cursor: pointer;
  display: inline-block;
  font-family: Arial;
  font-size: ${(props) => (props.$platform === "ios" ? "20px" : "20px")};
  margin: 0 5px 0 0;
  padding: 0 0 1px;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;

  ${(props) =>
    props.$platform === "ios" &&
    `
    width: 14px;
    height: 14px;
    line-height: 14px;
    font-size: 16px;
    font-weight: 100;
    color: ${colors.lightGray};
    text-shadow: none;

    &:active,
    &:hover {
      color: ${colors.lightGray};
    }
  `}

  ${(props) =>
    props.$platform === "android" &&
    `
    width: 14px;
    height: 14px;
    line-height: 14px;
    font-size: 16px;
    font-weight: 100;
    color: ${colors.lightGray};
    text-shadow: none;

    &:active,
    &:hover {
      color: ${colors.lightGray};
    }
  `}

  ${(props) =>
    props.$platform !== "ios" &&
    props.$platform !== "android" &&
    `
    &:active,
    &:hover {
      color: ${colors.lightestGray};
    }
  `}
`;

interface ISmartBannerIconProps {
  $platform: "ios" | "android";
}

export const SmartBannerIcon = styled(Box)<ISmartBannerIconProps>`
  -moz-background-size: cover;
  -moz-border-radius: 10px;
  -ms-flex-align: center;
  -ms-flex-pack: center;
  -o-background-size: cover;
  -webkit-align-items: center;
  -webkit-background-size: cover;
  -webkit-border-radius: 10px;
  -webkit-box-align: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  align-items: center;
  background-size: cover;
  border-radius: 10px;
  color: white;
  display: -ms-flexbox;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  font-size: 24px;
  font-weight: bold;
  height: 57px;
  justify-content: center;
  margin-right: 12px;
  width: 57px;
`;

interface ISmartBannerInfoProps {
  $platform: "ios" | "android";
}

export const SmartBannerInfo = styled(Box)<ISmartBannerInfoProps>`
  display: inline-block;
  flex: 1;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2em;
  vertical-align: middle;
  white-space: normal;
  width: calc(99% - 201px);

  ${(props) =>
    props.$platform === "ios" &&
    `
    color: ${colors.gray};
    text-shadow: none;
    font-weight: 100;
  `}

  ${(props) =>
    props.$platform === "android" &&
    `
    color: ${colors.gray};
    text-shadow: none;
    font-weight: 100;
  `}
`;

interface ISmartBannerTitleProps {
  $platform: "ios" | "android";
}

export const SmartBannerTitle = styled(Box)<ISmartBannerTitleProps>`
  font-size: 13px;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${(props) =>
    props.$platform === "ios" &&
    `
    color: ${colors.white};
    font-weight: 500;
  `}

  ${(props) =>
    props.$platform === "android" &&
    `
    color: ${colors.white};
    font-weight: 500;
  `}
`;

export const SmartBannerDescription = styled(Box)`
  color: ${colors.nearWhite};
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SmartBannerWrapper = styled(Box)`
  -ms-flex-align: center;
  -ms-flex-pack: center;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  align-items: center;
  display: -ms-flexbox;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  justify-content: center;
  max-width: 110px;
  width: 100%;
`;

interface ISmartBannerButtonProps {
  $platform: "ios" | "android";
}

export const SmartBannerButton = styled(Button)<ISmartBannerButtonProps>`
  ${(props) =>
    props.$platform === "ios" &&
    `
    min-width: 80px;
    height: 32px;
    font-weight: 500;
    padding: 8px 16px;
    background: ${colors.whiteAlpha90};
    color: ${colors.ned};
    border-radius: 20px;
    box-shadow: 0 1px 3px ${colors.blackAlpha12}, 0 1px 2px ${colors.blackAlpha24};
    transition: all 0.2s ease;
    font-size: 14px;
    text-transform: none;
    text-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active,
    &:hover {
      background: ${colors.white};
      box-shadow: 0 2px 6px ${colors.blackAlpha15}, 0 2px 4px ${colors.blackAlpha12};
      transform: translateY(-1px);
    }
  `}

  ${(props) =>
    props.$platform === "android" &&
    `
    min-width: 80px;
    height: 32px;
    font-weight: 500;
    padding: 8px 16px;
    background: ${colors.whiteAlpha90};
    color: ${colors.ned};
    border-radius: 20px;
    box-shadow: 0 1px 3px ${colors.blackAlpha12}, 0 1px 2px ${colors.blackAlpha24};
    transition: all 0.2s ease;
    font-size: 14px;
    text-transform: none;

    &:active,
    &:hover {
      background: ${colors.white};
      box-shadow: 0 2px 6px ${colors.blackAlpha15}, 0 2px 4px ${colors.blackAlpha12};
      transform: translateY(-1px);
    }
  `}

  ${(props) =>
    props.$platform !== "ios" &&
    props.$platform !== "android" &&
    `
    &:active,
    &:hover {
      color: ${colors.lightestGray};
    }
  `}
`;

export const SmartBannerButtonText = styled.span`
  -moz-box-shadow: none;
  -ms-flex-align: center;
  -ms-flex-pack: center;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -webkit-box-pack: center;
  -webkit-box-shadow: none;
  -webkit-justify-content: center;
  align-items: center;
  background: transparent;
  box-shadow: none;
  color: inherit;
  display: -ms-flexbox;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  font-weight: 500;
  height: 100%;
  justify-content: center;
  text-align: center;
  text-shadow: none;
  text-transform: none;

  &:active,
  &:hover {
    background: transparent;
  }
`;

export const SmartBannerImage = styled.img`
  -moz-border-radius: 8px;
  -webkit-border-radius: 8px;
  border-radius: 8px;
  height: 50px;
  width: 50px;
`;
